import axios from "axios";
import { SchemeMasterItem, SubSchemeMasterItem } from "../types/SchemeDataEntry.type";

const API_URL = import.meta.env.VITE_APP_API_URL;

const getData = (response: any) => {
	const data = response.data.data || response.data;
	if (Array.isArray(data)) return data;
	if (data && data.items && Array.isArray(data.items)) return data.items;
	return [];
};

export const fetchSchemes = async (): Promise<SchemeMasterItem[]> => {
	const response = await axios.get(`${API_URL}/admin/master/schemes/list`);
	return getData(response);
};

export const fetchSubSchemesByScheme = async (
	schemeId: number,
): Promise<SubSchemeMasterItem[]> => {
	const response = await axios.get(`${API_URL}/admin/sub-scheme/list`, {
		params: { scheme_id: schemeId, limit: 100 },
	});
	return getData(response);
};

export const fetchExpenditureYears = async () => {
	const response = await axios.get(`${API_URL}/admin/master/expenditure-years`);
	return getData(response);
};

export const fetchAuditYears = async () => {
	const response = await axios.get(`${API_URL}/admin/master/audit-years`);
	return getData(response);
};

// Latest finalized (NIRNAY Status = Yes) audit calendar entry for a scheme +
// the exact District/Block/Panchayat/Gram Sabha Date the user has picked —
// used to auto-fill the rest of Section B: Basic Details (Round No, Audit
// Start/End Date, SA Period From/To Date, Audit Year) on a new MGNREGS/
// PMAY-G Scheme Data Entry instead of the user re-entering what's already
// planned. District/Block/Panchayat/Gram Sabha Date stay user-selected — a
// district can have several audit calendar entries (one per Panchayat), so
// matching on district alone would pick the wrong one.
export const fetchLatestAuditCalendar = async (
	schemeId: number | string,
	districtId: number | string,
	blockId: number | string,
	villageId: number | string,
	sgsDate: string,
): Promise<any> => {
	const response = await axios.get(`${API_URL}/admin/audit-calendar/latest`, {
		params: {
			scheme_id: schemeId,
			district_id: districtId,
			block_id: blockId,
			village_id: villageId,
			sgs_date: sgsDate,
		},
	});
	return response.data?.data ?? null;
};

// Same as fetchLatestAuditCalendar above, but for school-based schemes
// (NMP, CMBFS) — matched by District/UDISE Code/School Name/Gram Sabha Date
// instead of District/Block/Panchayat/Gram Sabha Date.
export const fetchLatestAuditCalendarForSchool = async (
	schemeId: number | string,
	districtId: number | string,
	udiseCode: string,
	schoolId: number | string,
	sgsDate: string,
): Promise<any> => {
	const response = await axios.get(`${API_URL}/admin/audit-calendar/latest`, {
		params: {
			scheme_id: schemeId,
			district_id: districtId,
			udise_code: udiseCode,
			school_id: schoolId,
			sgs_date: sgsDate,
		},
	});
	return response.data?.data ?? null;
};

export const submitSchemeDataEntry = async (data: any): Promise<any> => {
	const jsonData: any = {};
	const coreFields = ["scheme_id", "sub_scheme_id", "report_file"];

	Object.keys(data).forEach((key) => {
		if (!coreFields.includes(key)) {
			jsonData[key] = data[key];
		}
	});

	const payload = {
		scheme_id: data.scheme_id,
		sub_scheme_id: data.sub_scheme_id,
		report_file: data.report_file,
		json_data: jsonData,
	};

	const response = await axios.post(
		`${API_URL}/admin/scheme-data-entry/add`,
		payload,
	);
	if (response.data && (response.data.status === 0 || response.data.status === false)) {
		throw new Error(response.data.message || response.data.error || "Operation failed");
	}
	return response.data;
};

export const submitMGNREGSData = async (data: any): Promise<any> => {
	const formData = new FormData();
	const jsonData: any = {};
	const coreFields = ["scheme_id", "sub_scheme_id", "report_file"];

	// Same null-stringification hazard as updateMGNREGSData — skip a falsy
	// core field entirely instead of letting FormData.append() coerce it to
	// the literal text "null"/"undefined".
	Object.keys(data).forEach((key) => {
		if (coreFields.includes(key)) {
			if (data[key] !== null && data[key] !== undefined) {
				formData.append(key, data[key]);
			}
		} else {
			jsonData[key] = data[key];
		}
	});
	formData.append("json_data", JSON.stringify(jsonData));

	const response = await axios.post(
		`${API_URL}/admin/scheme-data-entry/mgnregs`,
		formData,
		{
			headers: {
				"Content-Type": "multipart/form-data",
			},
		},
	);
	if (response.data && (response.data.status === 0 || response.data.status === false)) {
		throw new Error(response.data.message || response.data.error || "Operation failed");
	}
	return response.data;
};

export const fetchMGNREGSSubmissions = async (
	page = 1,
	limit = 10,
): Promise<any> => {
	const response = await axios.get(
		`${API_URL}/admin/scheme-data-entry/list?page=${page}&limit=${limit}`,
	);
	const data = response.data.data || response.data;

	if (Array.isArray(data)) {
		return {
			items: data,
			totalItems: data.length,
			totalPages: 1,
			currentPage: 1,
		};
	}

	if (data && data.items) {
		return data;
	}

	return { items: [], totalItems: 0 };
};

export const fetchMGNREGSById = async (id: number | string): Promise<any> => {
	const response = await axios.get(
		`${API_URL}/admin/scheme-data-entry/view/${id}`,
	);
	return response.data.data || response.data;
};

export const updateMGNREGSData = async (
	id: number | string,
	data: any,
): Promise<any> => {
	const formData = new FormData();
	const jsonData: any = {};
	const coreFields = ["scheme_id", "sub_scheme_id", "report_file"];

	// FormData.append() stringifies non-Blob values via String(), which turns
	// null/undefined into the literal text "null"/"undefined" — so any core
	// field left null on a given save (most commonly report_file before a PDF
	// has actually been chosen) must be skipped entirely, not appended as-is.
	Object.keys(data).forEach((key) => {
		if (coreFields.includes(key)) {
			if (data[key] !== null && data[key] !== undefined) {
				formData.append(key, data[key]);
			}
		} else {
			jsonData[key] = data[key];
		}
	});
	formData.append("json_data", JSON.stringify(jsonData));

	const response = await axios.put(
		`${API_URL}/admin/scheme-data-entry/edit/${id}`,
		formData,
		{
			headers: {
				"Content-Type": "multipart/form-data",
			},
		},
	);
	if (response.data && (response.data.status === 0 || response.data.status === false)) {
		throw new Error(response.data.message || response.data.error || "Operation failed");
	}
	return response.data;
};

export const fetchEmployeeCodesByRoleId = async (roleId: number) => {
	const response = await axios.get(
		`${API_URL}/admin/master/employee-code/${roleId}`,
	);
	return getData(response);
};

export const fetchDistrictEmployeeByCode = async (empCode: string) => {
	const response = await axios.get(
		`${API_URL}/admin/master/district-employee/${empCode}`,
	);
	return response.data?.data;
};

export const fetchVillageEmployeeByMobile = async (mobileNo: string) => {
	const response = await axios.get(
		`${API_URL}/admin/master/village-employee/${mobileNo}`,
	);
	return response.data?.data;
};

export const fetchHlcRegistrationNumbers = async (
	districtId?: number | string | null,
	schemeId?: number | string | null,
) => {
	const response = await axios.get(
		`${API_URL}/admin/hlc/registration-numbers`,
		{
			params: {
				...(districtId ? { district_id: districtId } : {}),
				...(schemeId ? { scheme_id: schemeId } : {}),
			},
		},
	);
	return response.data?.data || [];
};

export const fetchUdiseCodesByDistrictId = async (
	districtId: number,
	schemeId?: number | string | null,
) => {
	const response = await axios.get(
		`${API_URL}/admin/master/udise-code/${districtId}`,
		{ params: schemeId ? { scheme_id: schemeId } : undefined },
	);
	return getData(response);
};

export const fetchSchoolListByUdiseCode = async (
	udiseCode: string,
	districtId?: number | string | null,
	schemeId?: number | string | null,
) => {
	const response = await axios.get(
		`${API_URL}/admin/master/school/${udiseCode}`,
		{
			params: {
				...(districtId ? { district_id: districtId } : {}),
				...(schemeId ? { scheme_id: schemeId } : {}),
			},
		},
	);
	return getData(response);
};

export const fetchIssueTypes = async (schemeId?: number | null) => {
	try {
		const url = schemeId
			? `${API_URL}/admin/issue-type/list?scheme_id=${schemeId}&limit=1000`
			: `${API_URL}/admin/issue-type/list?limit=1000`;
		const response = await axios.get(url);
		if (
			response.data &&
			(response.data.status === 1 || response.data.status === true)
		) {
			const items = response.data.data?.items || response.data.data;
			if (Array.isArray(items)) return items;
		}
	} catch (e) {
		// Fallback
	}
	const legacyUrl = schemeId
		? `${API_URL}/admin/master/issue-types?scheme_id=${schemeId}`
		: `${API_URL}/admin/master/issue-types`;
	const response = await axios.get(legacyUrl);
	return getData(response);
};

export const fetchIssueCategories = async (issueTypeId: number) => {
	try {
		const url = issueTypeId
			? `${API_URL}/admin/issue-category/list?issue_type_id=${issueTypeId}&limit=1000`
			: `${API_URL}/admin/issue-category/list?limit=1000`;
		const response = await axios.get(url);
		if (response.data && (response.data.status === 1 || response.data.status === true)) {
			const items = response.data.data?.items || response.data.data;
			if (Array.isArray(items)) return items;
		}
	} catch (e) {
		// Fallback
	}
	const response = await axios.get(
		`${API_URL}/admin/master/issue-categories/${issueTypeId || ""}`,
	);
	return getData(response);
};

export const fetchIssueSubCategories = async (categoryId: number) => {
	try {
		const url = categoryId
			? `${API_URL}/admin/issue-sub-category/list?issue_category_id=${categoryId}&limit=1000`
			: `${API_URL}/admin/issue-sub-category/list?limit=1000`;
		const response = await axios.get(url);
		if (response.data && (response.data.status === 1 || response.data.status === true)) {
			const items = response.data.data?.items || response.data.data;
			if (Array.isArray(items)) return items;
		}
	} catch (e) {
		// Fallback
	}
	const response = await axios.get(
		`${API_URL}/admin/master/issue-sub-categories/${categoryId || ""}`,
	);
	return getData(response);
};

export const uploadSchemeReport = async (
	file: File,
	type: string = "scheme-report",
) => {
	const formData = new FormData();
	formData.append("filename", file);

	return axios.post(`${API_URL}/upload/upload-single/${type}`, formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
		withCredentials: true,
	});
};
